import { AxiosResponse, CancelTokenSource } from "axios";
import { axiosInstance, MessageResponse } from "./common";
import { PartyInvolved } from "./PartiesInvolved";
import { CreateCaseStakeholderRequest } from "./creditAccounts";
import { CaseEvent } from "./events";
import { CaseStage } from "./caseStages";
import { Participation } from "./participants";
import { Account } from "./accounts";
import { CreditAccount } from "./credit_accounts";
import { Stakeholder } from "./stakeholders";
import { StakeholderRelationship } from "./stakeholder_relationships";

export enum CaseStatus {
  OPEN = "OPEN",
  IN_PROGRESS = "IN_PROGRESS",
  DECLARED_AS_FRAUD = "DECLARED_AS_FRAUD",
  DECLARED_AS_NO_FRAUD = "DECLARED_AS_NO_FRAUD",
  DECLARED_AS_ATTEMPTED_FRAUD = "DECLARED_AS_ATTEMPTED_FRAUD",
  CLOSED = "CLOSED",
  REJECTED = "REJECTED",
  DEACTIVATED = "DEACTIVATED",
}

export enum AccountType {
  TERM_LOAN = "TERM_LOAN",
  CASH_CREDIT_WC = "CASH_CREDIT_WC",
  BANK_GUARANTEE = "BANK_GUARANTEE",
  LETTER_OF_CREDIT = "LETTER_OF_CREDIT",
  OTHERS = "OTHERS",
}

export const ACCOUNT_TYPE_LABEL_MAP: Record<AccountType, string> = {
  [AccountType.TERM_LOAN]: "Term Loan",
  [AccountType.CASH_CREDIT_WC]: "Cash Credit (WC)",
  [AccountType.BANK_GUARANTEE]: "Bank Guarantee",
  [AccountType.LETTER_OF_CREDIT]: "Letter of Credit",
  [AccountType.OTHERS]: "Others",
};

export enum AreaOfFraud {
  CREDIT = "CREDIT",
  OPERATIONAL = "OPERATIONAL",
  CYBER = "CYBER",
}

export const AREA_OF_FRAUD_LABEL_MAP: Record<AreaOfFraud, string> = {
  [AreaOfFraud.CREDIT]: "Credit",
  [AreaOfFraud.OPERATIONAL]: "Operational",
  [AreaOfFraud.CYBER]: "Cyber",
};

export interface Case {
  uin: string;
  loan_related: boolean;
  loan_reference_ids?: string[];
  region: string;
  branch: string;
  zone: string;
  product?: string;
  loan_product_info: LoanProductInfo[];
  referrer: string;
  referral_date: string;
  investigator_email?: string;
  investigation_nature: string[];
  customer: string;
  parties_involved?: PartyInvolved[];
  primary_party: string;
  background: string;
  exposure_currency?: string;
  exposure?: number;
  source: string;
  party_type: string;
  employee_involvement: boolean;
  employee_ids: string[];
  case_id?: string;
  status?: CaseStatus;
  area_of_fraud?: AreaOfFraud;
  created_at?: string;
  last_updated_at?: string;
  supervising_authority?: string;
  forwarding_authority?: string;
  account_type: AccountType;
  forensic_auditor?: string;
  created_by?: string;
  dealing_authority?: string;
  io_app_authority?: string;
  events?: CaseEvent[];
  current_stage?: CaseStage;
  participations?: Participation[];
  credit_accounts?: CreditAccount[];
  accounts?: Account[];
  stakeholders?: Stakeholder[];
  stakeholder_relationships?: StakeholderRelationship[];
}

export interface CreateCaseRequest {
  case: Case;
  case_initiator_id?: string;
  forwarding_auth_id: string;
  parties_involved: PartyInvolved[];
  credit_accounts?: CreditAccount[];
  accounts?: Account[];
  stakeholders?: CreateCaseStakeholderRequest[];
}

export interface CreateCaseResponse {
  case_id: string;
  uin: string;
}

export interface GetCasesParams {
  creation_start_date?: string;
  creation_end_date?: string;
  referral_start_date?: string;
  referral_end_date?: string;
  status?: CaseStatus[];
  area_of_fraud?: AreaOfFraud[];
  investigator_email?: string[];
  investigator_id?: string[];
  zone?: string[];
  case_id?: string[];
  page_size?: number;
  page_number?: number;
  order_by?: string;
  order?: string;
  uin?: string[];
}

export interface GetCasesResponse {
  cases: Case[];
}

export interface UpdateCaseRequest {
  exposure_currency?: string;
  exposure?: number;
  parties_involved?: string[];
  loan_product_info?: LoanProductInfo[];
  background?: string;
  status?: CaseStatus;
  employee_ids?: string[];
  forensic_auditor?: string;
}

export interface LoanProductInfo {
  loan_reference_id: string;
  product: string;
}

const CasesAPI = {
  create: async (request: CreateCaseRequest) => {
    const response: AxiosResponse<CreateCaseResponse> =
      await axiosInstance.post("/v1/cases", request);
    return response.data;
  },

  get: async (
    params: GetCasesParams,
    cancelTokenSource?: CancelTokenSource,
  ) => {
    const response: AxiosResponse<GetCasesResponse> = await axiosInstance.get(
      "/v1/cases",
      {
        params: params,
        paramsSerializer: {
          indexes: null,
        },
        cancelToken: cancelTokenSource?.token,
      },
    );
    return response.data;
  },

  update: async (caseId: string, request: UpdateCaseRequest) => {
    const response: AxiosResponse<MessageResponse> = await axiosInstance.patch(
      `/v1/cases/${caseId}`,
      request,
    );
    return response.data;
  },

  downloadLegacy: async (params: GetCasesParams) => {
    const response = await axiosInstance.get("/v1/cases/exports", {
      params: params,
      paramsSerializer: {
        indexes: null,
      },
      responseType: "blob",
    });

    return response.data;
  },

  download: async (params: GetCasesParams) => {
    const response = await axiosInstance.get("/v2/cases/exports", {
      params: params,
      paramsSerializer: {
        indexes: null,
      },
      responseType: "blob",
    });

    return response.data;
  },
};

export default CasesAPI;
